import { BrCard, BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import ErrorMessage from 'components/errorMessage/errorMessage';
import SelectBaseCalculo from 'components/selectBaseCalculo/selectBaseCalculo';
import { ArrecadacaoContext } from 'contexts/ArrecadacaoContext';
import { IBaseCalculoTodas, IBasesCalculoPrimeiraSaidaPorVenda, PaginasOutrosRegimes } from 'interfaces/IBasesCalculo';
import { IErrorResponseWithSentry } from 'interfaces/IErrorWithSentryId';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import React, { useContext, useEffect } from 'react';
import { formatCurrency } from 'utils/formatCurrency';
import ListaBalneario from '../listaBalneario/listaBalneario';
import ListaOutrasBases from '../listaOutrasBases/listaOutrasBases';
import styles from './dadosOperacoes.module.scss';

interface IDadosOperacoesProps {
  setIsAdicaoNovaBaseCalculo: React.Dispatch<React.SetStateAction<boolean>>;
  isViewOnly?: boolean;
  setBaseDeCalculoEdicao: React.Dispatch<React.SetStateAction<PaginasOutrosRegimes | ''>>;
  setIsEdicaoBaseCalculo: React.Dispatch<React.SetStateAction<boolean>>;
}

const DadosOperacoes = ({
  setIsAdicaoNovaBaseCalculo,
  isViewOnly,
  setBaseDeCalculoEdicao,
  setIsEdicaoBaseCalculo,
}: IDadosOperacoesProps) => {
  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const { getBasesCalculoTodas } = useBackEndRequests();

  const context = useContext(ArrecadacaoContext);

  useEffect(() => {
    setBaseDeCalculoEdicao('');
  }, [setBaseDeCalculoEdicao]);

  useEffect(() => {
    context?.setIdBalneario(-1);
    context?.setIdOutrosRegimes(-1);
    context?.setIdNCMOutrosRegimes(-1);
  }, []);

  const entrarModoEdicao = (chaveBaseCalculo: PaginasOutrosRegimes) => {
    setBaseDeCalculoEdicao(chaveBaseCalculo);
    setIsAdicaoNovaBaseCalculo(true);
  };

  const {
    data: listaTodasBases,
    error: listaTodasBasesError,
    isFetching: listaTodasBasesIsLoading,
    refetch: refetchlistaTodasBases,
  } = useQuery<IBaseCalculoTodas, IErrorResponseWithSentry>({
    queryKey: ['getBasesCalculoTodas', guiaRecolhimentoId],
    queryFn: () => getBasesCalculoTodas(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
    enabled: guiaRecolhimentoId !== '',
  });

  const primeiraSaidaPorVendaRender = (isViewOnly: boolean | undefined) => {
    const basesPrimeiraSaidaPorVenda: IBasesCalculoPrimeiraSaidaPorVenda | undefined =
      listaTodasBases?.primeiraSaidaPorVenda;
    const allEmpty =
      !basesPrimeiraSaidaPorVenda ||
      (basesPrimeiraSaidaPorVenda.balnearioComBanho.length === 0 &&
        basesPrimeiraSaidaPorVenda.balnearioSemBanho.length === 0 &&
        basesPrimeiraSaidaPorVenda.receitaBrutaVendas.length === 0 &&
        basesPrimeiraSaidaPorVenda.receitaCalculadaExportacoes.length === 0 &&
        basesPrimeiraSaidaPorVenda.vendaNacional.length === 0);
    if (allEmpty) {
      return <>Ainda não há formações de base de cálculo adicionadas para Primeira Venda</>;
    } else {
      return (
        <>
          {(basesPrimeiraSaidaPorVenda?.balnearioComBanho.length > 0 ||
            basesPrimeiraSaidaPorVenda?.balnearioSemBanho.length > 0) && (
            <ListaBalneario
              setIsEdicao={setIsEdicaoBaseCalculo}
              setBaseDeCalculo={setBaseDeCalculoEdicao}
              itensComBanho={basesPrimeiraSaidaPorVenda?.balnearioComBanho}
              itensSemBanho={basesPrimeiraSaidaPorVenda?.balnearioSemBanho}
              refetchLista={refetchlistaTodasBases}
              setIsAdicaoNovaBaseCalculo={setIsAdicaoNovaBaseCalculo}
              isViewOnly={isViewOnly}
            />
          )}
          {basesPrimeiraSaidaPorVenda?.receitaBrutaVendas.length > 0 && (
            <ListaOutrasBases
              setIsEdicao={setIsEdicaoBaseCalculo}
              setBaseDeCalculo={setBaseDeCalculoEdicao}
              baseCalculo={basesPrimeiraSaidaPorVenda.receitaBrutaVendas[0].itens}
              idBaseCalculo={basesPrimeiraSaidaPorVenda.receitaBrutaVendas[0].id}
              tipoBaseCalculo={'receitaBruta'}
              refetchLista={refetchlistaTodasBases}
              setIsAdicaoNovaBaseCalculo={setIsAdicaoNovaBaseCalculo}
              isViewOnly={isViewOnly}
            />
          )}
          {basesPrimeiraSaidaPorVenda?.receitaCalculadaExportacoes.length > 0 && (
            <ListaOutrasBases
              setIsEdicao={setIsEdicaoBaseCalculo}
              setBaseDeCalculo={setBaseDeCalculoEdicao}
              baseCalculo={basesPrimeiraSaidaPorVenda.receitaCalculadaExportacoes[0].itens}
              idBaseCalculo={basesPrimeiraSaidaPorVenda.receitaCalculadaExportacoes[0].id}
              tipoBaseCalculo={'receitaExportacoes'}
              refetchLista={refetchlistaTodasBases}
              setIsAdicaoNovaBaseCalculo={setIsAdicaoNovaBaseCalculo}
              isViewOnly={isViewOnly}
            />
          )}
          {basesPrimeiraSaidaPorVenda?.vendaNacional.length > 0 && (
            <ListaOutrasBases
              setIsEdicao={setIsEdicaoBaseCalculo}
              setBaseDeCalculo={setBaseDeCalculoEdicao}
              baseCalculo={basesPrimeiraSaidaPorVenda.vendaNacional[0].itens}
              idBaseCalculo={basesPrimeiraSaidaPorVenda.vendaNacional[0].id}
              tipoBaseCalculo={'vendaNacional'}
              refetchLista={refetchlistaTodasBases}
              setIsAdicaoNovaBaseCalculo={setIsAdicaoNovaBaseCalculo}
              isViewOnly={isViewOnly}
            />
          )}
        </>
      );
    }
  };

  const consumoRender = () => {
    const basesConsumo = listaTodasBases?.consumo;
    if (!basesConsumo?.receitaBrutaCalculada || basesConsumo?.receitaBrutaCalculada.length == 0) {
      return <>Ainda não há formações de base de cálculo adicionadas para Consumo</>;
    } else {
      return (
        <>
          {basesConsumo?.receitaBrutaCalculada.length > 0 && (
            <ListaOutrasBases
              setIsEdicao={setIsEdicaoBaseCalculo}
              setBaseDeCalculo={setBaseDeCalculoEdicao}
              baseCalculo={basesConsumo.receitaBrutaCalculada[0].itens}
              idBaseCalculo={basesConsumo.receitaBrutaCalculada[0].id}
              tipoBaseCalculo={'receitaBrutaCalculada'}
              refetchLista={refetchlistaTodasBases}
              setIsAdicaoNovaBaseCalculo={setIsAdicaoNovaBaseCalculo}
              isViewOnly={isViewOnly}
            />
          )}
        </>
      );
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.cardContainer}>
        <BrCard className={styles.card}>
          <div className={styles.cardContent}>
            <span className={styles.cardTitle}>Receita Total</span>
            <span className={styles.cardValue}>{formatCurrency(listaTodasBases?.valorReceitaTotal || 'R$ 0,00')}</span>
          </div>
        </BrCard>
        <BrCard className={styles.card}>
          <div className={styles.cardContent}>
            <span className={styles.cardTitle}>Deduções</span>
            <span className={styles.cardValue}>{formatCurrency(listaTodasBases?.valorTributosTotal || 'R$ 0,00')}</span>
          </div>
        </BrCard>
        <BrCard className={styles.card}>
          <div className={styles.cardContent}>
            <span className={styles.cardTitle}>Base de Cálculo</span>
            <span className={styles.cardValue}>
              {formatCurrency(listaTodasBases?.valorBaseCalculoTotal || 'R$ 0,00')}
            </span>
          </div>
        </BrCard>
      </div>
      {!isViewOnly && (
        <SelectBaseCalculo entrarModoEdicao={entrarModoEdicao} baseCalculoCadastradas={listaTodasBases} />
      )}
      <>
        {listaTodasBasesIsLoading ? (
          <BrLoading large />
        ) : (
          <>
            {listaTodasBasesError ? (
              <ErrorMessage
                errorMessage={'Erro ao carregar dados das Bases de Cálculo. Por favor, tente novamente.'}
                onClickFunction={() => {
                  refetchlistaTodasBases();
                }}
              />
            ) : (
              <>
                <BrCard className={styles.cardSecao}>
                  <div className={styles.cardSecaoContent}>
                    <span className={styles.cardSecaoTitle}>Primeira Saída por Venda</span>

                    <div className={styles.cardSecaoList}>{primeiraSaidaPorVendaRender(isViewOnly)}</div>
                    <div className={styles.cardValues}>
                      <span>
                        Receita Total:{' '}
                        {formatCurrency(listaTodasBases?.primeiraSaidaPorVenda?.valorReceitaTotal || 'R$ 0,00')}
                      </span>
                      <span>
                        Deduções Totais:{' '}
                        {formatCurrency(listaTodasBases?.primeiraSaidaPorVenda?.valorTributosTotal || 'R$ 0,00')}
                      </span>
                      <span>
                        Base de Cálculo Total:{' '}
                        {formatCurrency(listaTodasBases?.primeiraSaidaPorVenda?.valorBaseCalculoTotal || 'R$ 0,00')}
                      </span>
                    </div>
                  </div>
                </BrCard>
                <BrCard className={styles.cardSecao}>
                  <div className={styles.cardSecaoContent}>
                    <span className={styles.cardSecaoTitle}>Consumo</span>
                    <div className={styles.cardSecaoList}>{consumoRender()}</div>
                    <div className={styles.cardValues}>
                      <span>
                        Receita Total: {formatCurrency(listaTodasBases?.consumo?.valorReceitaTotal || 'R$ 0,00')}
                      </span>
                      <span>
                        Deduções Totais: {formatCurrency(listaTodasBases?.consumo?.valorTributosTotal || 'R$ 0,00')}
                      </span>
                      <span>
                        Base de Cálculo Total:{' '}
                        {formatCurrency(listaTodasBases?.consumo?.valorBaseCalculoTotal || 'R$ 0,00')}
                      </span>
                    </div>
                  </div>
                </BrCard>
              </>
            )}
          </>
        )}
      </>
    </div>
  );
};

export default DadosOperacoes;
